/* eslint-disable react/no-array-index-key */
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getPageLinkByNodeId, LinkModel, mapLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FitImage from "@ucs/Components/Common/FitImage";
import ChevronRight from "@ucs/Icons/ChevronRight";
import setIsLocalSelectedPLP from "@ucs/Store/Custom/SetIsLocalSelectedPLP";
import UCSColors from "@ucs/Utils/UCSColors";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    category = "category",
    categoryElements = "categoryElements",
    cta = "cta",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.category]: string[];
        [fields.categoryElements]: LinkModel[];
        [fields.cta]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const links = mapLinks<LinkModel, { openInNewWindow: boolean; overriddenTitle: string }>(
        state,
        ownProps.fields.categoryElements,
        widgetLink => ({
            openInNewWindow: widgetLink.fields.openInNewWindow,
            overriddenTitle: widgetLink.fields.overriddenTitle,
        }),
    );

    const categoryState = getCategoryState(state, ownProps.fields.category[0]);

    return {
        links,
        category: categoryState.value,
    };
};

const mapDispatchToProps = {
    setIsLocalSelectedPLP
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>

const imageSize = {
    maxWidth: "430px",
    maxHeight: "321px",
    minWidth: "300px",
    minHeight: "187px",
};

export interface ApplicationTileStyles {
    card?: InjectableCss;
    content?: InjectableCss;
    subCategories?: InjectableCss;
    linksColumn?: InjectableCss;
    link?: LinkProps;
}

export const applicationTileStyles: ApplicationTileStyles = {
    card: {
        css: css`
            width: 100%;           
            box-shadow: 0px 2px 10px 0px #0000000d;
             ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding: 0 20px;
                    `,
                    css``,
                    css``,
                    css``,
                    css``,
                ]);
            }}
        `,
    },
    content: {
        css: css`
            padding: 20px 16px;
            background: ${UCSColors.gray10};
            font-size: 17px;
            height: 440px;
            width: 100%;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    null,
                    css`
                        padding: 24px;
                        font-size: 18px;
                    `,
                ]);
            }}
        `,
    },
    subCategories: {
        css: css`
            width: 100%;
            display: flex;
            gap: 25px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        gap: 20px;
                    `,
                    css`
                        gap: 20px;
                    `,
                ]);
            }}
        `,
    },
    linksColumn: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 22px;
            gap: 5px;
        `,
    },
    link: {
        icon: {
            iconProps: {
                size: 16,
                color: "primary",
                css: css`
                    margin-right: 12px;
                `,
                src: ChevronRight,
            },
            position: "left",
        },
        typographyProps: {
            css: css`
                overflow: hidden;
            `,
        },
        css: css`
            align-items: center;
            height: 3em;
        `,
    },
};

const StyledLink = styled.a`
    position: absolute;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 50.8%, rgba(0, 0, 0, 0.65) 100%);    
    display: flex;
    align-items: flex-end;
    width: 100%;
    height: 100%;
    min-height: ${imageSize.minHeight};
    padding: 20px 24px;
    font-size: 22px;
    font-weight: 400;
    color: ${UCSColors.gray10};
    line-height: 1.1em;
    ${({ theme }) =>
        breakpointMediaQueries(theme, [
            css``,
            css``,
            css`
                font-size: 24px;
            `,
            css`
                font-size: 28px;
            `,
            css`
                font-size: 32px;
            `,
        ])};
`;

const styles = applicationTileStyles;

const ApplicationTile: React.FC<Props> = ({ fields, links, category, setIsLocalSelectedPLP }) => {
    const links1 = links.slice(0, 4);
    const links2 = links.slice(4, 8);

    const goToCategory = () => {
        window.location.href = category ? category.path : "/";
    };

    const clickHandler = (url: any) => {
        setIsLocalSelectedPLP({ isLocalSelectedPLP: true });
        const locationFilterId = getCookie("LocationBasedAttributeValue") || "";
        if (url.includes("catalogue") || url.includes("catalog")) {
            setIsLocalSelectedPLP({ isLocalSelectedPLP: true });
            if (locationFilterId !== "") {
                window.location.href = `${url}?attributeValueIds=${locationFilterId}`;
            }
            else {
                window.location.href = url;
            }
        } else {
            setIsLocalSelectedPLP({ isLocalSelectedPLP: false });
            window.location.href = url;
        }
    }

    const imageContainerStyles = {
        css: css`
            flex: 1;
            width: 100%;
            position: relative;
            overflow: hidden;            
            height: calc(29vw * 0.79);
            min-height: ${imageSize.minHeight};
            max-height: ${imageSize.maxHeight};
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-height: ${imageSize.minHeight};
                    `,
                    css`
                        max-height: ${imageSize.minHeight};
                    `,
                    css`
                        max-height: ${imageSize.minHeight};
                    `,
                    css``,
                    css``,
                ])};
        `,
    };

    return (         
        <StyledWrapper {...styles.card} data-test-selector="card">
            {category && (
                <FitImage image={category.smallImagePath} focalPoint="center" containerStyles={imageContainerStyles}>
                    <StyledLink href={category.path} onClick={() => setIsLocalSelectedPLP({ isLocalSelectedPLP: true })}>
                         <span>{category.name}</span>
                     </StyledLink>
                </FitImage>
               
            )}
            <StyledWrapper {...styles.content}>
                {category && (
                    <Typography as="p" style={{ paddingBottom: 22, hyphens: "auto" }}>
                        {category.properties.extendedDescription}
                    </Typography>
                )}
                <StyledWrapper {...styles.subCategories}>
                    <StyledWrapper
                        {...styles.linksColumn}
                        style={{ maxWidth: `${links2?.length > 0 ? "50%" : "100%"}` }}
                    >
                        {links1 &&
                            links1.map((link, idx) => {
                                return (
                                    <Link
                                        key={idx}
                                        href={link.url}
                                        target={link.openInNewWindow ? "_blank" : ""}
                                        {...styles.link}
                                        onClick={() => clickHandler(link.url)}
                                    >
                                        {link.overriddenTitle || link.title}
                                    </Link>
                                );
                            })}
                    </StyledWrapper>
                    <StyledWrapper {...styles.linksColumn} style={{ maxWidth: "50%" }}>
                        {links2 &&
                            links2.map((link, idx) => {
                                return (
                                    <Link
                                        key={idx}
                                        href={link.url}
                                        target={link.openInNewWindow ? "_blank" : ""}
                                        {...styles.link}
                                        onClick={() => clickHandler(link.url)}
                                    >
                                        {link.overriddenTitle || link.title}
                                    </Link>
                                );
                            })}
                    </StyledWrapper>
                </StyledWrapper>
                <Button
                    variant="secondary"
                    sizeVariant="small"
                    typographyProps={{ transform: "uppercase" }}
                    onClick={goToCategory}
                    style={{ marginTop: "auto" }}
                >
                    {fields.cta}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ApplicationTile),
    definition: {
        group: "Categories",
        fieldDefinitions: [
            {
                name: fields.category,
                displayName: "Categories",
                editorTemplate: "CategoriesField",
                fieldType: "General",
                defaultValue: [],
                isRequired: true,
                sortOrder: 1,
            },
            {
                name: fields.categoryElements,
                displayName: "Sub Categories / Products",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        return value; // TODO ISC-10781 make this work
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                sortOrder: 2,
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Category",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
            {
                name: fields.cta,
                displayName: "CTA",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                sortOrder: 3,
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
